import React, { useState } from 'react';
import {
    Box,
    Grid,
    Typography,
    TextField,
    Button,
    Alert,
    Paper
} from '@mui/material';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:8000/api';

const Login = ({ onLoginSuccess }) => {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            // Encode credentials as requested
            const encodedUsername = btoa(username);
            const encodedPassword = btoa(password);

            const response = await fetch(API_BASE + '/login', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                },
                body: JSON.stringify({
                    username: encodedUsername,
                    password: encodedPassword
                })
            });

            const data = await response.json();

            if (response.ok && data.success) {
                onLoginSuccess(data.user);
            } else {
                setError(data.message || 'Login failed. Please check your credentials.');
            }
        } catch (err) {
            console.error('Login error:', err);
            setError('An error occurred while connecting to the server.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <style>
                {`
                    body, html {
                        margin: 0 !important;
                        padding: 0 !important;
                        overflow-x: hidden;
                    }
                `}
            </style>
            <Box sx={{
                minHeight: '100vh',
                width: '100vw',
                display: 'flex',
                overflow: 'hidden',
                margin: 0,
                padding: 0,
                boxSizing: 'border-box'
            }}>
                <Grid container sx={{
                    flex: 1,
                    width: '100%',
                    height: '100vh',
                    margin: 0,
                    padding: 0
                }} spacing={0}>
                    {/* Left Section - Login Form with Gradient Background */}
                    <Grid
                        item
                        xs={12}
                        md={7}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                            minHeight: { xs: '50vh', md: '100vh' },
                            height: '100%',
                            width: { md: '60%' },
                            flexBasis: { md: '60%' },
                            maxWidth: { md: '60%' },
                            padding: { xs: 4, md: 6 },
                            position: 'relative',
                            overflow: 'hidden',
                            '&::before': {
                                content: '""',
                                position: 'absolute',
                                top: '-50%',
                                left: '-50%',
                                width: '200%',
                                height: '200%',
                                background: 'radial-gradient(circle, rgba(255,255,255,0.15) 0%, transparent 70%)',
                                animation: 'pulse 8s ease-in-out infinite'
                            },
                            '@keyframes pulse': {
                                '0%, 100%': {
                                    transform: 'scale(1)',
                                    opacity: 1
                                },
                                '50%': {
                                    transform: 'scale(1.1)',
                                    opacity: 0.8
                                }
                            }
                        }}
                    >
                        <Paper
                            elevation={0}
                            sx={{
                                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                borderRadius: 3,
                                padding: { xs: 3, md: 4 },
                                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
                                position: 'relative',
                                zIndex: 1,
                                width: '100%',
                                maxWidth: 350
                            }}
                        >
                            <Box sx={{ mb: 4, textAlign: 'center' }}>
                                <Typography
                                    variant="h4"
                                    component="h1"
                                    fontWeight="700"
                                    color="#1e3c72"
                                    gutterBottom
                                    sx={{
                                        fontSize: { xs: '1.75rem', md: '2rem' }
                                    }}
                                >
                                    Welcome Back
                                </Typography>
                                <Typography
                                    variant="body1"
                                    color="text.secondary"
                                    sx={{
                                        fontWeight: 400,
                                        fontSize: { xs: '0.9rem', md: '1rem' }
                                    }}
                                >
                                    Please sign in to continue
                                </Typography>
                            </Box>

                            {error && (
                                <Alert
                                    severity="error"
                                    sx={{
                                        mb: 3,
                                        borderRadius: 2
                                    }}
                                >
                                    {error}
                                </Alert>
                            )}

                            <form onSubmit={handleSubmit}>
                                <TextField
                                    fullWidth
                                    label="Username"
                                    variant="outlined"
                                    margin="normal"
                                    value={username}
                                    onChange={(e) => setUsername(e.target.value)}
                                    autoFocus
                                    required
                                    sx={{
                                        mb: 2,
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: 2,
                                            backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                            '&:hover fieldset': {
                                                borderColor: '#4facfe',
                                            },
                                            '&.Mui-focused fieldset': {
                                                borderWidth: 2,
                                                borderColor: '#4facfe',
                                            }
                                        }
                                    }}
                                />
                                <TextField
                                    fullWidth
                                    label="Password"
                                    type="password"
                                    variant="outlined"
                                    margin="normal"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                    sx={{
                                        mb: 3,
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: 2,
                                            backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                            '&:hover fieldset': {
                                                borderColor: '#4facfe',
                                            },
                                            '&.Mui-focused fieldset': {
                                                borderWidth: 2,
                                                borderColor: '#4facfe',
                                            }
                                        }
                                    }}
                                />

                                <Button
                                    type="submit"
                                    fullWidth
                                    variant="contained"
                                    size="large"
                                    disabled={loading}
                                    sx={{
                                        mt: 2,
                                        py: 1.5,
                                        fontSize: '1.05rem',
                                        fontWeight: 700,
                                        textTransform: 'none',
                                        borderRadius: 2,
                                        background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                                        boxShadow: '0 4px 15px rgba(79, 172, 254, 0.4)',
                                        transition: 'all 0.3s ease',
                                        '&:hover': {
                                            background: 'linear-gradient(135deg, #00f2fe 0%, #4facfe 100%)',
                                            boxShadow: '0 6px 20px rgba(79, 172, 254, 0.5)',
                                            transform: 'translateY(-2px)'
                                        },
                                        '&:active': {
                                            transform: 'translateY(0)'
                                        }
                                    }}
                                >
                                    {loading ? 'Signing in...' : 'Sign In'}
                                </Button>
                            </form>
                        </Paper>
                    </Grid>

                    {/* Right Section - Logo with Light Background */}
                    <Grid
                        item
                        xs={12}
                        md={5}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: '#f9f9f9',
                            minHeight: { xs: '50vh', md: '100vh' },
                            height: '100%',
                            width: { md: '40%' },
                            flexBasis: { md: '40%' },
                            maxWidth: { md: '40%' },
                        }}
                    >
                        <Box
                            sx={{
                                textAlign: 'center',
                                width: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                        >
                            <Box
                                component="img"
                                src="/assets/ys-group-logo.png"
                                alt="YS Group Logo"
                                sx={{
                                    width: '100%',
                                    maxWidth: { xs: 250, md: 350 },
                                    height: 'auto',
                                    filter: 'drop-shadow(0 4px 12px rgba(0, 0, 0, 0.08))'
                                }}
                            />
                            <Typography
                                variant="h3"
                                component="h2"
                                sx={{
                                    fontWeight: 800,
                                    color: '#1a1a1a',
                                    mb: 1.5,
                                    fontSize: { xs: '2rem', md: '2.75rem' },
                                    letterSpacing: '0.02em'
                                }}
                            >
                                {/* YS GROUP */}
                            </Typography>
                            <Typography
                                variant="h6"
                                sx={{
                                    color: '#666',
                                    fontWeight: 500,
                                    fontSize: { xs: '0.85rem', md: '1rem' },
                                    letterSpacing: '0.05em',
                                    mt: 2
                                }}
                            >
                                Software Developed by e.Sense InfoSolutions | Support: 9921946393
                            </Typography>
                        </Box>
                    </Grid>
                </Grid>
            </Box>
        </>
    );
};

export default Login;
